import React from 'react'

import { useIsRTL } from '@context/SessionSettingsContext'

import { IconFCType } from '@ui/icons/types'

import { DIRECTION_LABELS, Direction } from './icon-direction'
import { ValidIconProps, createValidIcon } from './type-helper'

type DirectionKey = keyof typeof DIRECTION_LABELS
type DirectionStatics = { [K in DirectionKey]: IconFCType<Omit<WithDirection, 'direction'>> }

type WithDirection = { direction?: Direction; noFlip?: boolean }

function getFlippedDirection(direction: Direction): Direction {
  switch (direction) {
    case 'left':
      return 'right'
    case 'right':
      return 'left'
    case 'upLeft':
      return 'upRight'
    case 'upRight':
      return 'upLeft'
    case 'downLeft':
      return 'downRight'
    case 'downRight':
      return 'downLeft'
    default:
      return direction
  }
}

export function withDirectionalStatics(
  Base: React.FC<ValidIconProps & WithDirection>
): IconFCType<WithDirection> & DirectionStatics {
  const WrappedBase: React.FC<ValidIconProps & WithDirection> = (props) => {
    const isRTL = useIsRTL()
    const { direction, noFlip, ...rest } = props
    const flippedDir = isRTL && !noFlip && direction ? getFlippedDirection(direction) : direction

    return <Base {...rest} direction={flippedDir} />
  }

  const out = createValidIcon<WithDirection>(WrappedBase) as IconFCType<WithDirection> &
    Partial<DirectionStatics>

  const entries = Object.entries(DIRECTION_LABELS) as Array<[DirectionKey, Direction]>
  for (const [key, dir] of entries) {
    out[key] = createValidIcon((props: ValidIconProps & WithDirection) => {
      const isRTL = useIsRTL()
      const flippedDir = isRTL && !props.noFlip ? getFlippedDirection(dir) : dir
      return <Base {...props} direction={flippedDir} />
    })
  }

  return out as IconFCType<WithDirection> & DirectionStatics
}
